// MARK: API
import * as api from "@startapp/loc-admin-api";

// MARK: Mobx
import { action, computed, observable } from "mobx";

// MARK: Resources
import strings from "../../resources/strings";
import { cutLongStrings } from "../../resources/cutLongStrings";

// MARK: Resources
import VariableChangeHandler from "../_helpers/VariableChangeHandler";
import { IRowItem } from "../../components/Table/TableRow";

// MARK: Stores
import { routerStore, uiStore } from "../_rootStore";

export default class CommentStore extends VariableChangeHandler {

	// Controls
	@observable public commentLoading: boolean = false;
	@observable public commentPageOffset: number = 0;
	@observable public comments: api.PostComment[] =  [];
	@observable public comment: api.PostComment | null = null;
	@observable public isOpenModal: boolean = false;
	@observable public user: api.User | null = null;

	// Variables
	@observable public userId: string = "";
	@observable public text: string = "";

	@computed
	public get loading(): boolean {
		return [
			this.commentLoading,
		].reduce((l, r) => l || r);
	}

	@computed
	public get header() {
		const header = strings.comment.table.header;
		return [
			header.comment,
			header.author,
			header.nick,
			header.createdAt,
		];
	}

	private static formatComment(comment: api.PostComment): IRowItem {
		return {
			id: comment.id,
			name: comment.text,
			columns: [
				{
					value: cutLongStrings(comment.text),
				},
				{
					value: comment.userInfo.name,
				},
				{
					value: comment.userInfo.nick,
				},
				{
					value: strings.formatter.date.date(comment.createdAt),
				},
			],
		};
	}

	@computed
	public get commentTableRows(): IRowItem[] {
		return this.comments.map(CommentStore.formatComment);
	}

	@action
	public closeModal = () => this.isOpenModal = false;

	@action
	public openModal = () => this.isOpenModal = true;

	@action
	public getComment = async (commentId: string) => {
		if (this.commentLoading) {
			return;
		}
		this.commentLoading = true;
		try {
			this.comment = await api.getCommentForId(commentId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.commentLoading = false;
		}
	};

	@action
	public getComments = async (pageOffset: number) => {
		if (this.commentLoading) {
			return;
		}
		this.commentLoading = true;
		try {
			this.comments = await api.getAllComment(pageOffset);
			this.commentPageOffset = pageOffset;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.commentLoading = false;
		}
	};

	@action
	public nextPage = async () => {
		await this.getComments(this.commentPageOffset + 1);
	};

	@action
	public redirectToCommentDetails = (id: string) => {
		routerStore.push(`/dashboard/comments/details/${id}`);
	};

	@action
	public redirectToCommentTable = () => {
		routerStore.push(`/dashboard/comments/`);
	};

	@action
	public previousPage = async () => {
		if (this.commentPageOffset > 0) {
			await this.getComments(this.commentPageOffset - 1);
		}
	};

	@action
	public openDeleteCommentDialog = async (commentId: string) => {
		uiStore.showDialog(
			strings.comment.deleteCommentDialog,
			() => {
				this.deleteComment(commentId);
				this.redirectToCommentTable();
			},
		);
	};

	@action
	public deleteComment = async (commentId: string) => {
		if (this.commentLoading) {
			return;
		}
		this.commentLoading = true;
		try {
			await api.deleteCommentPost(commentId);
			this.comments = this.comments.filter((item) => item.id !== commentId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.commentLoading = false;
		}
	};
}
